/**
 * Author: Claude Code
 * Generation: Agent creation preview using global Phaser preload service
 * Version: v3.2.45
 * Modified: 2025-11-04
 */
import React, { useEffect, useRef } from 'react';
import * as Phaser from 'phaser';
import { PhaserPreloadService } from '@services/PhaserPreloadService';
import { SpriteAssetLoader } from '@game/animation/SpriteAssetLoader';
import { colorToCharacterType, getCharacterFrameKey } from '@game/animation/utils/spriteKeyUtils';
import { AnimationType, CharacterType } from '@game/animation/spriteTypes';

interface SlimePreviewProps { color: string; size?: number }

// Proper Scene class for preview rendering
class PreviewScene extends Phaser.Scene {
  private color: string;
  private size: number;
  private charType: CharacterType;
  private firstKey: string;
  private animKey: string;

  constructor(color: string, size: number) {
    super('SlimePreviewScene');
    this.color = color;
    this.size = size;
    this.charType = colorToCharacterType(color) as unknown as CharacterType;
    this.firstKey = getCharacterFrameKey(this.charType, AnimationType.IDLE, 0);
    this.animKey = `global_${this.charType}_idle`;
  }

  preload() {
    // Try to load animation if global preload isn't ready yet (fallback)
    if (!this.textures.exists(this.firstKey)) {
      const loader = new SpriteAssetLoader(this);
      loader.loadCharacterAnimation(this.charType, AnimationType.IDLE);
    }
  }

  create() {
    // Configure pixel art
    try { this.game.config.pixelArt = true as any; } catch {}
    try { (this.cameras?.main as any).roundPixels = true; } catch {}

    // Check if textures exist (either from global preload or local loading)
    if (this.textures.exists(this.firstKey)) {
      const sprite = this.add.sprite(this.scale.width / 2, this.scale.height / 2, this.firstKey);
      const SCALE = Math.min((this.size * 0.9) / 48, 2);
      sprite.setScale(SCALE);
      try { (sprite as any).setOrigin?.(0.5, 0.5); } catch {}

      // Create animation definition locally if it doesn't exist (Phaser Scenes have separate animation registries)
      if (!this.anims.exists(this.animKey)) {
        const frames: { key: string; frame: any }[] = [];
        for (let i = 0; i < 11; i++) {
          frames.push({ key: getCharacterFrameKey(this.charType, AnimationType.IDLE, i), frame: null });
        }
        this.anims.create({ key: this.animKey, frames, frameRate: 10, repeat: -1 });
      }

      // Play animation
      try { (sprite as any).anims?.play(this.animKey); } catch {}
    } else {
      console.warn(`SlimePreview: Texture not found: ${this.firstKey}`);
    }
  }
}

export const SlimePreview: React.FC<SlimePreviewProps> = ({ color, size = 128 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | null>(null);
  const lastColorRef = useRef<string>('');

  useEffect(() => {
    if (!containerRef.current) return;

    const mount = () => {
      if (!containerRef.current) return;

      try {
        const config: Phaser.Types.Core.GameConfig = {
          type: Phaser.AUTO,
          width: size,
          height: size,
          backgroundColor: 'rgba(0,0,0,0)',
          parent: containerRef.current,
          pixelArt: true,
          render: { pixelArt: true, antialias: false, transparent: true },
          scene: new PreviewScene(color, size)
        };
        gameRef.current = new Phaser.Game(config);
        lastColorRef.current = color;
      } catch (error) {
        console.error('SlimePreview: Failed to create Phaser instance:', error);
      }
    };

    const unmount = () => {
      if (gameRef.current) {
        try {
          gameRef.current.destroy(true);
        } catch (error) {
          console.warn('SlimePreview: Error destroying Phaser instance:', error);
        }
        gameRef.current = null;
      }
    };

    // Mount or remount if color changed
    if (!gameRef.current) {
      mount();
    } else if (lastColorRef.current !== color) {
      unmount();
      mount();
    }

    return () => {
      unmount();
    };
  }, [color, size]);

  return <div ref={containerRef} style={{ width: size, height: size }} />;
};

export default SlimePreview;

